import React, { useEffect, useMemo } from "react";
import * as THREE from "three";
import { Floor3DShapeDefinition } from "../structures/floors/floorGeometry";

const FLOOR_CONTACT_WIDTH = 0.36;
const FLOOR_CONTACT_ALPHA = 0.16;
const FLOOR_Z = 0.014;
const MIN_EDGE_LENGTH = 0.08;

const AmbientOcclusionOverlay: React.FC<{
  floors: Floor3DShapeDefinition[];
  wallHeight: number;
  hiddenFloorIds?: Set<string>;
}> = ({ floors, wallHeight, hiddenFloorIds }) => {
  const visibleFloors = useMemo(
    () => floors.filter((floor) => !hiddenFloorIds?.has(floor.id)),
    [floors, hiddenFloorIds],
  );
  const { floorGeometry, wallGeometry } = useMemo(
    () => createAmbientOcclusionGeometrySet(visibleFloors, wallHeight),
    [visibleFloors, wallHeight],
  );

  const floorMaterial = useMemo(() => createOcclusionMaterial(true), []);
  const wallMaterial = useMemo(() => createOcclusionMaterial(true), []);

  useEffect(() => () => floorGeometry.dispose(), [floorGeometry]);
  useEffect(() => () => wallGeometry.dispose(), [wallGeometry]);
  useEffect(() => () => floorMaterial.dispose(), [floorMaterial]);
  useEffect(() => () => wallMaterial.dispose(), [wallMaterial]);

  if (
    floorGeometry.getAttribute("position").count === 0 &&
    wallGeometry.getAttribute("position").count === 0
  ) {
    return null;
  }

  return (
    <>
      {floorGeometry.getAttribute("position").count > 0 && (
        <mesh
          geometry={floorGeometry}
          material={floorMaterial}
          renderOrder={8}
          raycast={() => null}
        />
      )}
      {wallGeometry.getAttribute("position").count > 0 && (
        <mesh
          geometry={wallGeometry}
          material={wallMaterial}
          renderOrder={9}
          raycast={() => null}
        />
      )}
    </>
  );
};

function createOcclusionMaterial(depthTest: boolean): THREE.ShaderMaterial {
  const shaderMaterial = new THREE.ShaderMaterial({
    uniforms: {
      color: { value: new THREE.Color(0x191713) },
      opacity: { value: 1 },
    },
    vertexShader: `
        attribute float aoAlpha;
        varying float vAoAlpha;

        void main() {
          vAoAlpha = aoAlpha;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
    fragmentShader: `
        uniform vec3 color;
        uniform float opacity;
        varying float vAoAlpha;

        void main() {
          gl_FragColor = vec4(color, vAoAlpha * opacity);
        }
      `,
    transparent: true,
    depthWrite: false,
    depthTest,
    side: THREE.DoubleSide,
    toneMapped: false,
  });
  shaderMaterial.polygonOffset = true;
  shaderMaterial.polygonOffsetFactor = -1;
  shaderMaterial.polygonOffsetUnits = -1;
  shaderMaterial.onBeforeRender = () => {
    shaderMaterial.uniforms.opacity.value = shaderMaterial.opacity;
  };
  return shaderMaterial;
}

export function createAmbientOcclusionGeometrySet(
  floors: Floor3DShapeDefinition[],
  _wallHeight: number,
): {
  floorGeometry: THREE.BufferGeometry;
  wallGeometry: THREE.BufferGeometry;
} {
  const floorPositions: number[] = [];
  const floorAlphas: number[] = [];
  const floorIndices: number[] = [];
  const wallPositions: number[] = [];
  const wallAlphas: number[] = [];
  const wallIndices: number[] = [];

  floors.forEach((floor) => {
    const points = getUniqueShapePoints(floor.shape);
    if (points.length < 3) return;

    const winding = signedArea(points) >= 0 ? 1 : -1;

    for (let index = 0; index < points.length; index++) {
      const start = points[index];
      const end = points[(index + 1) % points.length];
      const edge = end.clone().sub(start);
      const length = edge.length();
      if (length < MIN_EDGE_LENGTH) continue;

      const direction = edge.clone().divideScalar(length);
      const inward = getInteriorNormal(direction, winding);
      const floorWidth = Math.min(FLOOR_CONTACT_WIDTH, length * 0.45);
      const floorOffset = inward.clone().multiplyScalar(floorWidth);

      pushQuad(
        floorPositions,
        floorAlphas,
        floorIndices,
        vector3(start.x, start.y, FLOOR_Z),
        vector3(end.x, end.y, FLOOR_Z),
        vector3(end.x + floorOffset.x, end.y + floorOffset.y, FLOOR_Z),
        vector3(start.x + floorOffset.x, start.y + floorOffset.y, FLOOR_Z),
        FLOOR_CONTACT_ALPHA,
        FLOOR_CONTACT_ALPHA,
        0,
        0,
      );
    }
  });

  return {
    floorGeometry: createBufferGeometry(
      floorPositions,
      floorAlphas,
      floorIndices,
    ),
    wallGeometry: createBufferGeometry(wallPositions, wallAlphas, wallIndices),
  };
}

function createBufferGeometry(
  positions: number[],
  alphas: number[],
  indices: number[],
): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("aoAlpha", new THREE.Float32BufferAttribute(alphas, 1));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  return geometry;
}

function pushQuad(
  positions: number[],
  alphas: number[],
  indices: number[],
  a: THREE.Vector3,
  b: THREE.Vector3,
  c: THREE.Vector3,
  d: THREE.Vector3,
  alphaA: number,
  alphaB: number,
  alphaC: number,
  alphaD: number,
) {
  const index = positions.length / 3;
  positions.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z, d.x, d.y, d.z);
  alphas.push(alphaA, alphaB, alphaC, alphaD);
  indices.push(index, index + 1, index + 2, index, index + 2, index + 3);
}

function getUniqueShapePoints(shape: THREE.Shape): THREE.Vector2[] {
  const points = shape.getPoints(48);
  if (points.length <= 1) return points;
  const first = points[0];
  const last = points[points.length - 1];
  if (first.distanceToSquared(last) < 1e-10) {
    return points.slice(0, -1);
  }
  return points;
}

function getInteriorNormal(direction: THREE.Vector2, winding: 1 | -1) {
  const normal = new THREE.Vector2(-direction.y, direction.x);
  return winding > 0 ? normal : normal.multiplyScalar(-1);
}

function signedArea(points: THREE.Vector2[]) {
  let area = 0;
  for (let index = 0; index < points.length; index++) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    area += current.x * next.y - next.x * current.y;
  }
  return area / 2;
}

function vector3(x: number, y: number, z: number) {
  return new THREE.Vector3(x, y, z);
}

export default AmbientOcclusionOverlay;
